import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from '../api.service';
import { PagePreviewResponseModel, CustomerPageDatas, PagePreviewRequestModel } from '../../models/customerPageDatas.model';
import { CustomerPageData } from '../../models/customerPageData.model';
import { Observable, catchError, concatMap, finalize, map, of, shareReplay, tap, throwError } from 'rxjs';
import { FileData } from '../../models/fileData.model';
import { CustomerPageDataUpdate } from '../../models/customerPageDataUpdate.model';
import { customerPageDataInsert } from '../../models/customerPageDataInsert.model';
import { RefBusinessLists } from '../../models/refBusinessLists.model';

@Injectable({
  providedIn: 'root'
})
export class CustomerPageDataService {
  constructor(private http: HttpClient, private apiService: ApiService) { }
  private fileRequests: Map<string, Observable<FileData>> = new Map();  // Cache for ongoing/completed requests
  private saveRequests: Map<string, Observable<string>> = new Map();
  private pendingValues: Map<string, any[]> = new Map();
  apiUrl = `${this.apiService.apiUrl}/customerPageDatas`;

  save(rawData: CustomerPageData, valuesToSave: any[], completionPercent: number): Observable<string> {
    const isUpdate = rawData.id && rawData.id !== "00000000-0000-0000-0000-000000000000";
    const key = `${rawData.page.id}_${rawData.component.id}_${rawData.dataGroupId}_${rawData.blockIteration}`;

    if (!isUpdate && this.saveRequests.has(key)) {
      // when was requested 2 adds
      // save last values in case of update
      this.pendingValues.set(key, valuesToSave);
      return this.saveRequests.get(key)!;
    }

    let obs$: Observable<string>;

    if (isUpdate) {
      const updatePayload: CustomerPageDataUpdate = {
        values: valuesToSave,
        completionPercent: completionPercent
      };

      obs$ = this.updateCustomerPageData(rawData.id!, updatePayload).pipe(
        map(() => rawData.id!),
        catchError(err => {
          return throwError(() => err);
        })
      );
    } else {
      const insertData: CustomerPageData = {
        id: rawData.id,
        customer: { id: rawData.customer.id },
        page: { id: rawData.page.id },
        component: {
          id: rawData.component.id,
          type: rawData.component.type,
          config: {
            inputType: rawData.component.config?.inputType,
            fileType: rawData.component.config?.fileType
          }
        },
        blockIteration: rawData.blockIteration,
        groupId: rawData.groupId,
        dataGroupId: rawData.dataGroupId,
        values: valuesToSave,
        connectionOutStatus: rawData.connectionOutStatus,
        source: "" //will be set on backend
      };
      const data: customerPageDataInsert = {
        data: insertData,
        completionPercent: completionPercent
      }
      if(insertData.values && insertData.values.length > 0 && insertData.values.some(x => x && x !== "")){
        obs$ = this.addCustomerPageData(data).pipe(
          tap(newId => { if (newId) rawData.id = newId; }), // Updates instance that made the save
          // If more than one request was sent to add, update only the latest one
          concatMap(newId => {
            if (this.pendingValues.has(key)) {
              console.log("update after add");
              const latestValues = this.pendingValues.get(key)!;
              this.pendingValues.delete(key);
              return this.updateCustomerPageData(newId, {
                values: latestValues,
                completionPercent
              }).pipe(
                map(() => newId),
                catchError(err => {
                  return throwError(() => err);
                })
              );
            }
            return of(newId);
          }),
          shareReplay(1),
          finalize(() => {
            this.saveRequests.delete(key);
            this.pendingValues.delete(key);
          }),
          catchError(err => {
            this.saveRequests.delete(key);
            this.pendingValues.delete(key);
            return throwError(() => err);
          })
        );

        this.saveRequests.set(key, obs$);
      }
      else{
        return of('');
      }
    }

    return obs$;
  }

  getCustomerPageDatas(pageId: string, customerId: string): Observable<CustomerPageDatas> {
    return this.http.get<CustomerPageDatas>(`${this.apiUrl}/customer/${customerId}/page/${pageId}`);
  }

  getCustomerPagePreviewDatas(data: PagePreviewRequestModel): Observable<PagePreviewResponseModel> {
    return this.http.post<PagePreviewResponseModel>(`${this.apiUrl}/preview`, data);
  }

  addCustomerPageData(data: customerPageDataInsert): Observable<string> {
    return this.http.post<string>(`${this.apiUrl}`, data);
  }

  updateCustomerPageData(customerPageDataId: string, data: CustomerPageDataUpdate): Observable<void> {
    return this.http.patch<void>(`${this.apiUrl}/${customerPageDataId}`, data);
  }

  deleteCustomerPageData(customerPageDataId: string, completionPercent: number): Observable<boolean>{
    return this.http.post<boolean>(`${this.apiUrl}/${customerPageDataId}`, completionPercent);
  }

  uploadFile(customerPageData: CustomerPageData, formFile: File): Observable<FileData> {
    const formData = new FormData();
    formData.append('file', formFile);
    formData.append('customerPageDataJson', JSON.stringify(customerPageData));
    return this.http.post<FileData>(`${this.apiUrl}/uploadFile`, formData);
  }

  downloadFile(customerPageDataId: string, azureFileName: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${customerPageDataId}/downloadFile/${azureFileName}`, { responseType: 'blob' });
  }

  getFile(customerPageDataId: string, azureFileName: string): Observable<FileData> {
    const cacheKey = `${customerPageDataId}_${azureFileName}`; // Unique key for each file

    if (!this.fileRequests.has(cacheKey)) {
      const request$ = this.http
        .get<FileData>(`${this.apiUrl}/${customerPageDataId}/getFile/${azureFileName}`)
        .pipe(
          catchError((error) => {
            // Throw an error to notify subscribers of the failure
            throw error;
          }),
          shareReplay(1) // Cache and share the result
        );

      this.fileRequests.set(cacheKey, request$);
    }

    return this.fileRequests.get(cacheKey)!;
  }
  deleteFile(customerPageDataId: string, target: string, completionPercent: number): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl}/${customerPageDataId}/deleteFile/${target}`, completionPercent); //2 possible controller methods
  }

  getRefBusinessLists(): Observable<RefBusinessLists> {
    return this.http.get<RefBusinessLists>(`${this.apiUrl}/refBusiness`);
  }
}
